import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardBody,
  Table,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
  Container,
  Badge,
  Input,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import apiService from 'service/apiService';
import ApplicationDetailsModal from './components/ApplicationDetailsModal';
import SuccessModal from 'components/SuccessModal';
import SearchFilters from './components/SearchFilters';
import { formatNumber } from '../../utils/formatUtils';

const ApplicationsList = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [applicationDetails, setApplicationDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [statusCounts, setStatusCounts] = useState({
    INCOMPLETE: 0,
    DUPLICATED: 0,
  });
  const statusOptions = [
    { value: 'INCOMPLETE', label: 'Incomplete', color: '#5e72e4' },
    { value: 'DUPLICATED', label: 'Duplicated', color: '#5e72e4' },
  ];

  const [successOpen, setSuccessOpen] = useState(false);
  const [successTitle, setSuccessTitle] = useState('Confirmation');
  const [successMessage, setSuccessMessage] = useState('');
  const openSuccess = (message, title) => {
    setSuccessMessage(message);
    if (title) setSuccessTitle(title);
    setSuccessOpen(true);
  };
  const closeSuccess = () => setSuccessOpen(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteTargetMerchantId, setDeleteTargetMerchantId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [promoteLoadingId, setPromoteLoadingId] = useState(null);
  const [promoteConfirmOpen, setPromoteConfirmOpen] = useState(false);
  const [promoteTargetApp, setPromoteTargetApp] = useState(null);

  const [filters, setFilters] = useState({});
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [simpleSearch, setSimpleSearch] = useState('');
  const searchInputRef = useRef(null);
  
  // Control initial load and prevent double requests
  const initialLoadDone = useRef(false);
  const searchDebounceTimer = useRef(null);

  // Helper function to process paginated responses - useMemo to prevent recreation
  const processPaginatedResponse = useCallback((data, size) => {
    const pageSize = size || itemsPerPage;
    let applicationsArray = [];
    let newTotalElements = 0;
    let newTotalPages = 0;

    if (data && data.content && Array.isArray(data.content)) {
      applicationsArray = data.content;
      newTotalElements = data.totalElements || data.content.length;
      newTotalPages = data.totalPages || Math.ceil(newTotalElements / pageSize) || 0;
    } else if (Array.isArray(data)) {
      applicationsArray = data;
      newTotalElements = data.length;
      newTotalPages = Math.ceil(data.length / pageSize) || 1;
    } else {
      applicationsArray = [];
      newTotalElements = 0;
      newTotalPages = 0;
    }

    return { applicationsArray, newTotalElements, newTotalPages };
  }, [itemsPerPage]);

  // Main function to load applications
  const loadApplications = useCallback(async (status = null, page = 0) => {
    try {
      setLoading(true);
      setError(null);

      const data = status
        ? await apiService.getApplicationsByStatus(status, page, itemsPerPage)
        : await apiService.getApplications(page, itemsPerPage);

      const { applicationsArray, newTotalElements, newTotalPages } = processPaginatedResponse(data);
      
      setApplications(applicationsArray);
      setTotalElements(newTotalElements);
      setTotalPages(newTotalPages);
    } catch (err) {
      setError('Error loading applications: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, processPaginatedResponse]);

  // Helper functions
  const extractMerchantId = (app) => {
    try {
      return (
        app?.merchantId ||
        app?.merchant_id ||
        app?.merchant?.id ||
        app?.merchant?.merchantId ||
        app?.merchantPayload?.id ||
        app?.merchantPayload?.merchantId ||
        null
      );
    } catch (_) {
      return null;
    }
  };

  const openDeleteConfirm = (application) => {
    const appId = application?.id;
    const mId = extractMerchantId(application);
    setDeleteTargetId(appId);
    setDeleteTargetMerchantId(mId);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (deleteLoading) return;
    setDeleteConfirmOpen(false);
    setDeleteTargetId(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) {
      openSuccess('Application ID is missing.', 'Error');
      return;
    }
    if (!deleteTargetMerchantId) {
      openSuccess('Merchant ID is missing for this application.', 'Error');
      return;
    }
    setDeleteLoading(true);
    setError(null);
    try {
      const resp = await apiService.deleteApplication(deleteTargetId, deleteTargetMerchantId);
      const successMsg = (resp && (resp.message || resp.detail)) ? (resp.message || resp.detail) : 'Application deleted successfully.';
      openSuccess(successMsg, 'Success');
      await loadApplications(selectedStatus, currentPage);
    } catch (err) {
      const backendMsg = err?.originalMessage || err?.message || 'Failed to delete application.';
      openSuccess(backendMsg, 'Error');
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
      setDeleteTargetId(null);
      setDeleteTargetMerchantId(null);
    }
  };

  const handlePromote = async (application) => {
    try {
      setPromoteLoadingId(application.id);
      const resp = await apiService.promoteToDeal(application.id);
      const successMsg = (resp && (resp.message || resp.detail)) ? (resp.message || resp.detail) : 'Deal created successfully.';
      openSuccess(successMsg, 'Success');
      
      setTimeout(() => {
        loadApplications(selectedStatus, currentPage);
      }, 1500);
      
    } catch (err) {
      const backendMsg = err?.originalMessage || err?.message || 'Failed to create deal.';
      openSuccess(backendMsg, 'Error');
    } finally {
      setPromoteLoadingId(null);
      setPromoteConfirmOpen(false);
      setPromoteTargetApp(null);
    }
  };

  const openPromoteConfirm = (application) => {
    setPromoteTargetApp(application);
    setPromoteConfirmOpen(true);
  };

  const closePromoteConfirm = () => {
    if (promoteLoadingId) return;
    setPromoteConfirmOpen(false);
    setPromoteTargetApp(null);
  };

  const getIncompleteMissingSummary = (app) => {
    try {
      const details = app || {};
      const m = details?.merchantPayload || details?.merchant || {};
      const missing = [];
  
      if (!details.amount) missing.push('Amount');
      if (!details.totalSubscribers) missing.push('Total Subscribers');
      if (!details.totalMonthlyRevenue) missing.push('Total Monthly Revenue');
      if (!m.legalName) missing.push('Legal Name');
      if (!m.dbaName) missing.push('DBA Name');
      if (!(m.federalTaxId || m.federalTaxID)) missing.push('Federal Tax ID');
      if (!m.stateOfInc) missing.push('State of Incorporation');
      if (!m.industry) missing.push('Industry');
      if (!m.businessStructure) missing.push('Business Structure');
      if (!m.website) missing.push('Website');
      if (!m.dateEstablished) missing.push('Date Established');
      if (!m.type) missing.push('Type');
      if (!m.phoneNumber) missing.push('Phone Number');
  
      const addresses = Array.isArray(m.addresses) ? m.addresses : [];
      if (addresses.length < 1) {
        missing.push('Business Address');
      }
  
      const owners = Array.isArray(m.owners) ? m.owners : [];
      if (owners.length < 1) {
        missing.push('Owner');
        missing.push('Owner Address');
        missing.push('Owner Contact');
      } else {
        const hasOwnerAddress = owners.some(
          (o) => Array.isArray(o.addresses) && o.addresses.length > 0
        );
  
        const hasOwnerWithContacts = owners.some((o) => {
          const contacts = Array.isArray(o.contacts)
            ? o.contacts
            : Array.isArray(o.contacs)
            ? o.contacs
            : [];
          const hasPrimaryEmail = contacts.some(
            (c) =>
              (c.type || '').toLowerCase() === 'email' &&
              (c.isPrimary === true || c.isPrimary === 'true')
          );
          const hasPrimaryPhone = contacts.some(
            (c) =>
              (c.type || '').toLowerCase() === 'phone' &&
              (c.isPrimary === true || c.isPrimary === 'true')
          );
          return hasPrimaryEmail && hasPrimaryPhone;
        });
  
        if (!hasOwnerAddress) missing.push('Owner Address');
        if (!hasOwnerWithContacts) {
          missing.push('Owner must have primary Email and Phone');
        }
      }
  
      const banks = Array.isArray(m.banks) ? m.banks : [];
      if (banks.length < 1) {
        missing.push('Bank Information');
      }
  
      return missing.length > 0
        ? `Missing: ${missing.join(', ')}`
        : 'All required fields present';
    } catch (_) {
      return 'Missing information summary unavailable';
    }
  };

  // Load applications - only on initial mount
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      
      const loadInitialApplications = async () => {
        try {
          setLoading(true);
          setError(null);
          const data = await apiService.getApplications(0, itemsPerPage);
          
          let applicationsArray = [];
          let newTotalElements = 0;
          let newTotalPages = 0;

          if (data && data.content && Array.isArray(data.content)) {
            applicationsArray = data.content;
            newTotalElements = data.totalElements || data.content.length;
            newTotalPages = data.totalPages || Math.ceil(newTotalElements / itemsPerPage) || 0;
          } else if (Array.isArray(data)) {
            applicationsArray = data;
            newTotalElements = data.length;
            newTotalPages = Math.ceil(data.length / itemsPerPage) || 1;
          }
          
          setApplications(applicationsArray);
          setTotalElements(newTotalElements);
          setTotalPages(newTotalPages);
        } catch (err) {
          setError('Error loading applications: ' + err.message);
        } finally {
          setLoading(false);
        }
      };
      
      loadInitialApplications();
    }
    
    // Cleanup debounce timer on unmount
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, []);

  // Load status counts on mount
  useEffect(() => {
    const loadCounts = async () => {
      try {
        const statuses = ['INCOMPLETE','DUPLICATED'];
        const results = await Promise.all(statuses.map(s => apiService.CountApplicationsByStatus(s).catch(() => 0)));
        const nextCounts = {};
        statuses.forEach((s, idx) => { nextCounts[s] = Number(results[idx] || 0); });
        setStatusCounts(nextCounts);
      } catch (_) {}
    };
    loadCounts();
  }, []);

  // Load applications when page or status changes (after initial load)
  useEffect(() => {
    // Skip if not initialized yet
    if (!initialLoadDone.current) return;
    
    // Skip if in advanced mode or simple search is active
    if (isAdvancedMode || simpleSearch.trim()) return;
    
    // Skip initial state (page 0 with no status selected) - handled by initial load
    if (currentPage === 0 && !selectedStatus) return;
    
    const loadApplications = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = selectedStatus
          ? await apiService.getApplicationsByStatus(selectedStatus, currentPage, itemsPerPage)
          : await apiService.getApplications(currentPage, itemsPerPage);

        let applicationsArray = [];
        let newTotalElements = 0;
        let newTotalPages = 0;

        if (data && data.content && Array.isArray(data.content)) {
          applicationsArray = data.content;
          newTotalElements = data.totalElements || data.content.length;
          newTotalPages = data.totalPages || Math.ceil(newTotalElements / itemsPerPage) || 0;
        } else if (Array.isArray(data)) {
          applicationsArray = data;
          newTotalElements = data.length;
          newTotalPages = Math.ceil(data.length / itemsPerPage) || 1;
        }
        
        setApplications(applicationsArray);
        setTotalElements(newTotalElements);
        setTotalPages(newTotalPages);
      } catch (err) {
        setError('Error loading applications: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadApplications();
  }, [currentPage, selectedStatus]);

  const handleNewApplication = () => {
    navigate('/wizard/application/new');
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setSelectedStatus(null);
    setIsAdvancedMode(Object.keys(newFilters || {}).length > 0);
  };

  const handleSearch = async (searchFilters = filters, page = 0, size = itemsPerPage) => {
    setError(null);
    setLoading(true);

    try {
      if (!searchFilters || Object.keys(searchFilters).length === 0) {
        setIsAdvancedMode(false);
        await loadApplications(selectedStatus, page);
        setLoading(false);
        return;
      }

      setIsAdvancedMode(true);

      const payload = { page, size };
      const opMap = (op) => (op === 'ILIKE' ? 'CONTAINS' : 'EQUALS');
      if (searchFilters.legalName) payload.legalName = { operation: opMap(searchFilters.legalName_operator), value: searchFilters.legalName };
      if (searchFilters.dbaName) payload.dbaName = { operation: opMap(searchFilters.dbaName_operator), value: searchFilters.dbaName };
      if (searchFilters.createdBy) payload.createdBy = { operation: opMap(searchFilters.createdBy_operator), value: searchFilters.createdBy };
      if (searchFilters.status) {
        const op = searchFilters.status_operator === 'ILIKE' ? 'CONTAINS' : 'EQUALS';
        payload.status = { operation: op, value: String(searchFilters.status).toUpperCase() };
      }
      if (searchFilters.federalTaxId) payload.federalTaxId = { operation: opMap(searchFilters.federalTaxId_operator), value: searchFilters.federalTaxId };
      if (searchFilters.createdAtFrom || searchFilters.createdAtTo) {
        const start = searchFilters.createdAtFrom ? `${searchFilters.createdAtFrom}T00:00:00Z` : null;
        const end = searchFilters.createdAtTo ? `${searchFilters.createdAtTo}T23:59:59Z` : null;
        if (start) payload.createdDate = { operation: 'GREATER_THAN_OR_EQUAL', value: start };
        if (end) payload.lastModifiedDate = { operation: 'LESS_THAN_OR_EQUAL', value: end };
      }

      const data = await apiService.advanceSearchApplications(payload);
      const { applicationsArray, newTotalElements, newTotalPages } = processPaginatedResponse(data, size);
      
      setCurrentPage(page);
      setApplications(applicationsArray);
      setTotalElements(newTotalElements);
      setTotalPages(newTotalPages);
      
    } catch (error) {
      console.error('Search failed:', error);
      setError(`Search failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFilters({});
    setSimpleSearch('');
    setError(null);
    setSelectedStatus(null);
    setCurrentPage(0);
    setIsAdvancedMode(false);
  };

  const toggleAdvancedSearch = () => {
    setShowAdvancedSearch(!showAdvancedSearch);
  };

  const handleSimpleSearch = (searchTerm) => {
    const raw = String(searchTerm || '').slice(0, 255);
    let next = raw;
    const pattern = /^\d{2}-/;
    if (pattern.test(raw)) {
      const digits = raw.replace(/\D/g, '');
      const firstTwo = digits.slice(0, 2);
      const rest = digits.slice(2, 9);
      next = `${firstTwo}${firstTwo ? '-' : ''}${rest}`;
    }
    setSimpleSearch(next);
    
    // Clear any existing timer
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }
    
    // Only debounce if initial load is done
    if (initialLoadDone.current) {
      searchDebounceTimer.current = setTimeout(async () => {
        const query = next.trim();
        
        if (!query) {
          // If empty, load all applications directly
          try {
            setLoading(true);
            setError(null);
            setSelectedStatus(null);
            setCurrentPage(0);
            
            const data = await apiService.getApplications(0, itemsPerPage);
            
            let applicationsArray = [];
            let newTotalElements = 0;
            let newTotalPages = 0;

            if (data && data.content && Array.isArray(data.content)) {
              applicationsArray = data.content;
              newTotalElements = data.totalElements || data.content.length;
              newTotalPages = data.totalPages || Math.ceil(newTotalElements / itemsPerPage) || 0;
            } else if (Array.isArray(data)) {
              applicationsArray = data;
              newTotalElements = data.length;
              newTotalPages = Math.ceil(data.length / itemsPerPage) || 1;
            }
            
            setApplications(applicationsArray);
            setTotalElements(newTotalElements);
            setTotalPages(newTotalPages);
          } catch (err) {
            setError(`Error loading applications: ${err.message}`);
          } finally {
            setLoading(false);
          }
        } else {
          // Search with the query
          try {
            setLoading(true);
            setError(null);
            const data = await apiService.generalSearchApplication(query, 0, itemsPerPage);
            
            let applicationsArray = [];
            let newTotalElements = 0;
            let newTotalPages = 0;

            if (data && data.content && Array.isArray(data.content)) {
              applicationsArray = data.content;
              newTotalElements = data.totalElements || data.content.length;
              newTotalPages = data.totalPages || Math.ceil(newTotalElements / itemsPerPage) || 0;
            } else if (Array.isArray(data)) {
              applicationsArray = data;
              newTotalElements = data.length;
              newTotalPages = Math.ceil(data.length / itemsPerPage) || 1;
            }
            
            setApplications(applicationsArray);
            setTotalElements(newTotalElements);
            setTotalPages(newTotalPages);
            setCurrentPage(0);
          } catch (err) {
            setError(`Search failed: ${err.message}`);
          } finally {
            setLoading(false);
          }
        }
      }, 500);
    }
  };

  const handleStatusClick = async (status) => {
    if (loading) return;
    const nextStatus = selectedStatus === status ? null : status;
    
    // Clear simple search when clicking status filter
    setSimpleSearch('');
    setSelectedStatus(nextStatus);
    setCurrentPage(0);
    
    // If not in advanced mode, load directly
    if (!isAdvancedMode) {
      await loadApplications(nextStatus, 0);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (loading) return;
    if (pageNumber < 0) return;
    if (totalPages && pageNumber >= totalPages) return;
    
    if (isAdvancedMode) {
      setLoading(true);
      handleSearch(filters, pageNumber, itemsPerPage);
    } else {
      setCurrentPage(pageNumber);
    }
  };

  const handleViewApplication = async (application) => {
    setModalOpen(true);
    setDetailsLoading(true);
    setApplicationDetails(null);
    
    try {
      const details = await apiService.getApplicationById(application.id);
      setApplicationDetails(details);
    } catch (err) {
      console.error('Error loading application details:', err);
      setError('Error loading application details: ' + err.message);
    } finally {
      setDetailsLoading(false);
    }
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (!modalOpen) {
      setApplicationDetails(null);
    }
  };

  const formatCurrency = (amount) => {
    return formatNumber(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const m = /^([0-1]\d)-([0-3]\d)-(\d{4}) (\d{2}):(\d{2}):(\d{2})$/.exec(String(dateString));
      let date;
      if (m) {
        const month = parseInt(m[1], 10) - 1;
        const day = parseInt(m[2], 10);
        const year = parseInt(m[3], 10);
        const hh = parseInt(m[4], 10);
        const mm = parseInt(m[5], 10);
        const ss = parseInt(m[6], 10);
        date = new Date(year, month, day, hh, mm, ss);
      } else {
        date = new Date(dateString);
      }
      if (isNaN(date.getTime())) return String(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (_) {
      return String(dateString);
    }
  };

  const currentApplications = applications;

  if (loading && applications.length === 0) {
    return (
      <Container className="mt-4 h-auto" fluid>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner color="primary" />
          <span className="ml-2">Loading applications...</span>
        </div>
      </Container>
    );
  }

  return (
    <>
      <style>
        {`
          .text-truncate {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 10rem;
          }

          @media (max-width: 767.98px) {
            .table-responsive {
              font-size: 0.875rem;
            }
            .compact-text {
              font-size: 0.8rem !important;
            }
            .compact-badge {
              font-size: 0.6rem !important;
              padding: 0.1rem 0.25rem !important;
            }
            .compact-icon {
              width: 24px !important;
              height: 24px !important;
              font-size: 0.7rem !important;
            }
            .btn-icon {
              padding: 0.5rem 0.75rem !important;
              font-size: 0.875rem !important;
              min-height: 38px !important;
              border-radius: 8px !important;
            }
            .card-header .btn-icon {
              width: 100% !important;
              justify-content: center !important;
              margin-bottom: 0.5rem !important;
              align-items: center !important;
              display: flex !important;
            }
            .card-header .btn-icon:last-child {
              margin-bottom: 0 !important;
            }
            .card-header .d-flex.flex-column {
              width: 100% !important;
            }
          }
          .applications-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255,255,255,0.6);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 2;
          }
        `}
      </style>
    <Container className="mt-4 h-auto" fluid>
        <Row className="mb-4">
        <Col>
            <Card className="bg-gradient-info text-white mb-0">
              <CardBody className="py-3">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
              <div className="mb-2 mb-md-0">
                    <h3 className="text-white mb-0">
                  <i className="ni ni-check-bold mr-2" />
                  Applications
                </h3>
                    <p className="text-white mb-0 mt-1" style={{ opacity: 0.9 }}>
                  Manage and track application submissions
                  {totalElements > 0 && (
                    <span className="ml-2 badge badge-light">
                      {formatNumber(totalElements)} application{totalElements !== 1 ? 's' : ''}
                    </span>
                  )}
                </p>
              </div>
                  <div className="d-flex flex-column flex-sm-row gap-2">
                <Button
                      color="light" 
                      size="sm"
                      onClick={toggleAdvancedSearch}
                      style={{ backgroundColor: '#e3f2fd', borderColor: '#bbdefb', color: '#1976d2' }}
                      className="mb-2 mb-sm-0"
                    >
                      <i className={`ni ${showAdvancedSearch ? 'ni-fat-remove' : 'ni-fat-add'} mr-2`}></i>
                      <span className="d-none d-sm-inline">
                        {showAdvancedSearch ? 'Hide Advanced Search' : 'Advanced Search'}
                      </span>
                      <span className="d-inline d-sm-none">
                        {showAdvancedSearch ? 'Hide Search' : 'Advanced'}
                      </span>
                    </Button>
                    <Button color="success" size="sm" onClick={handleNewApplication}>
                      <i className="ni ni-fat-add mr-2" />
                  <span className="d-none d-sm-inline">New Application</span>
                  <span className="d-inline d-sm-none">New</span>
              </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col lg="6" md="12" className="mb-3 mb-lg-0">
            <div style={{ maxWidth: '400px', width: '100%' }}>
              <div className="position-relative">
                <Input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search applications"
                  value={simpleSearch}
                  onChange={(e) => handleSimpleSearch(e.target.value)}
                  style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e9ecef',
                    borderRadius: '25px',
                    padding: '12px 20px',
                    fontSize: '0.9rem',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    width: '100%'
                  }}
                />
                <div className="position-absolute" style={{ right: '15px', top: '50%', transform: 'translateY(-50%)' }}>
                  <i className="ni ni-zoom-split-in text-muted" style={{ fontSize: '0.9rem' }}></i>
                </div>
                {simpleSearch && (
                  <Button
                    color="link"
                    size="sm"
                    onClick={() => handleSimpleSearch('')}
                    className="position-absolute p-0"
                    style={{ 
                      right: '40px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      color: '#6c757d',
                      fontSize: '0.8rem'
                    }}
                    title="Clear search"
                  >
                    <i className="ni ni-fat-remove"></i>
                  </Button>
                )}
              </div>
            </div>
          </Col>
          <Col lg="6" md="12">
            <div className="status-filters-container">
              <div className="d-flex align-items-center flex-nowrap">
                <span className="text-muted mr-2 status-label-text">
                  Filter by status:
                </span>
                <div className="status-filters-wrapper">
                  <div className="status-filters-scroll">
                    {statusOptions.map((status) => (
                      <button
                        key={status.value}
                        onClick={() => handleStatusClick(status.value)}
                        className={`status-filter-pill compact ${selectedStatus === status.value ? 'active' : ''}`}
                      >
                        <span className="status-label">{status.label}</span>
                        <span className="status-badge">
                          {formatNumber(statusCounts[status.value] ?? 0)}
                        </span>
                      </button>
                    ))}
                    {selectedStatus && (
                      <button
                        onClick={() => { 
                          setSelectedStatus(null); 
                          setFilters({}); 
                          setCurrentPage(0);
                          setSimpleSearch(''); 
                        }}
                        className="status-filter-pill clear-btn compact"
                      >
                        <i className="ni ni-fat-remove"></i>
                        <span>Clear</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {showAdvancedSearch && (
          <Row className="mb-4">
            <Col>
              <Card className="shadow">
                <CardHeader className="border-0">
                  <h3 className="mb-0">Search Criteria</h3>
            </CardHeader>
                <CardBody>
                  <SearchFilters 
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onSearch={handleSearch}
                    onClear={handleClear}
                    isLoading={loading}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        <Row>
          <Col>
            <Card className="shadow">
            <CardBody className="px-0 pt-0" style={{ position: 'relative' }}>
              {loading && (
                <div className="applications-overlay">
                  <div className="d-flex align-items-center">
                    <Spinner color="primary" />
                    <span className="ml-2 text-muted">Loading applications...</span>
                  </div>
                </div>
              )}
              {error && (
                <Alert color="danger" className="mx-3 mt-3">
                  <i className="fas fa-exclamation-triangle mr-2" />
                  {error}
                </Alert>
              )}
              
              {!loading && applications.length === 0 ? (
                <div className="text-center py-5">
                  <div className="mb-4">
                    <i className="ni ni-zoom-split-in text-muted" style={{ fontSize: '4rem', opacity: 0.3 }} />
                  </div>
                  {Object.keys(filters).length > 0 || simpleSearch ? (
                    <>
                      <h4 className="text-muted mb-2">No matches found</h4>
                      <p className="text-muted mb-4">No applications match the current search criteria.</p>
                    </>
                  ) : (
                    <>
                      <h4 className="text-muted mb-2">No applications found</h4>
                    </>
                  )}
                </div>
              ) : (!loading && (
                  <>
                    <div className="d-none d-md-block">
                      <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                        <Table className="table table-hover" responsive style={{ minWidth: '1100px', whiteSpace: 'nowrap' }}>
                    <thead className="thead-light">
                      <tr>
                            <th scope="col" className="border-0" style={{ width: '10%' }}>
                              <div className="d-flex align-items-center compact-text">
                                Actions
                              </div>
                            </th>
                            <th scope="col" className="border-0" style={{ width: '25%' }}>
                              <div className="d-flex align-items-center compact-text">
                                Business
                              </div>
                            </th>
                            <th scope="col" className="border-0" style={{ width: '20%' }}>
                              <div className="d-flex align-items-center compact-text">
                                Owner
                              </div>
                            </th>
                            <th scope="col" className="border-0" style={{ width: '15%' }}>
                              <div className="d-flex align-items-center compact-text">
                                Amount
                              </div>
                            </th>
                            <th scope="col" className="border-0" style={{ width: '15%' }}>
                              <div className="d-flex align-items-center compact-text">
                                Status
                              </div>
                            </th>
                            <th scope="col" className="border-0" style={{ width: '15%' }}>
                              <div className="d-flex align-items-center compact-text">
                                Created
                              </div>
                            </th>
                      </tr>
                    </thead>
                    <tbody>
                          {currentApplications.map((application, index) => {
                            const merchant = application.merchantPayload || {};
                            const firstOwner = (merchant.owners && merchant.owners[0]) || {};
                            const status = String(application.status || '').toUpperCase();
                            const rowStyle = (status === 'DUPLICATED') ? { backgroundColor: '#fdf3f3' } : {};
                        
                        return (
                              <tr key={application.id || index} className="border-bottom" style={rowStyle}>
                            <td className="border-0">
                                  <div className="d-flex align-items-center gap-1">
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => handleViewApplication(application)}
                                      className="btn-icon mr-1"
                                title="View Application Details"
                                    style={{
                                        borderRadius: '50%',
                                        width: '32px',
                                        height: '32px',
                                        padding: '0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      transition: 'all 0.2s ease'
                                    }}
                              >
                                      <i className="ni ni-single-copy-04" />
                                    </Button>
                                    {status === 'INCOMPLETE' && (
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => navigate('/wizard/application/update', { state: { application } })}
                                        className="btn-icon mr-1"
                                        title="Edit Application"
                                        style={{ borderRadius: '50%', width: '32px', height: '32px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                      >
                                        <i className="ni ni-settings-gear-65" />
                                      </Button>
                                    )}
                                    {(status === 'PENDING' || status === 'DUPLICATED') && (
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => openPromoteConfirm(application)}
                                        className="btn-icon mr-1"
                                        title="Promote to Deal"
                                        style={{ borderRadius: '50%', width: '32px', height: '32px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                      >
                                        {promoteLoadingId === application.id ? (<Spinner size="sm" />) : (<i className="ni ni-send" />)}
                                      </Button>
                                    )}
                                    {status === 'DUPLICATED' && (
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => openDeleteConfirm(application)}
                                        className="btn-icon"
                                        title="Delete Application"
                                        style={{ borderRadius: '50%', width: '32px', height: '32px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                      >
                                        <i className="ni ni-fat-remove" />
                              </Button>
                                    )}
                                  </div>
                            </td>
                            <td className="border-0">
                                  <div className="d-flex align-items-center">
                                    <div className="compact-text text-truncate">
                                      <div className="font-weight-bold text-dark mb-1">
                                   {merchant.legalName || 'N/A'}
                                       </div>
                                      <small className="text-muted">
                                      {merchant.industry || 'N/A'}
                                    </small>
                                </div>
                              </div>
                            </td>
                            <td className="border-0">
                                  <div className="d-flex align-items-center">
                                    <div className="compact-text text-truncate">
                                      <div className="font-weight-bold text-dark mb-1">
                                  {firstOwner.firstName || 'N/A'} {firstOwner.lastName || 'N/A'}
                                      </div>
                                  <small className="text-muted">
                                        {firstOwner.ownershipPercentage || 'N/A'}%
                                  </small>
                                </div>
                              </div>
                            </td>
                            <td className="border-0">
                              <div className="d-flex align-items-center">
                                    <div className="compact-text text-truncate">
                                      <span className="font-weight-bold text-dark">
                                        {formatCurrency(application.amount || 0)}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="border-0">
                                  <div className="compact-text text-truncate">
                                <Badge 
                                      color="light" 
                                      className="compact-badge text-uppercase"
                                      id={`status-badge-${application.id || index}`}
                                      style={{ fontSize: '0.6rem' }}
                                >
                                      {String(application.status || 'N/A').toUpperCase()}
                                </Badge>
                                    {status === 'INCOMPLETE' && (
                                      <UncontrolledTooltip placement="top" target={`status-badge-${application.id || index}`}>
                                        {getIncompleteMissingSummary(application)}
                                      </UncontrolledTooltip>
                                    )}
                                  </div>
                                </td>
                                <td className="border-0">
                                  <div className="d-flex align-items-center">
                                    <div className="compact-text text-truncate">
                                      <div className="font-weight-bold text-dark mb-1">
                                        {formatDate(application.createdDate || application.createdAt || application.created_at)}
                                      </div>
                                      <small className="text-muted">
                                        {application.createdBy || 'System'}
                                  </small>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                      </div>
                </div>

                    <div className="d-md-none">
                      <div className="row">
                        {currentApplications.map((application, index) => {
                          const merchant = application.merchantPayload || {};
                          const firstOwner = (merchant.owners && merchant.owners[0]) || {};
                          const businessAddress = (merchant.addresses && merchant.addresses[0]) || {};
                          const status = String(application.status || '').toUpperCase();
                          const cardStyle = (status === 'DUPLICATED') ? { backgroundColor: '#fdf3f3' } : {};
                          
                          return (
                            <div key={application.id || index} className="col-12 mb-3">
                              <Card className="shadow-sm border-0" style={cardStyle}>
                                <CardBody className="p-3">
                                  <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div className="d-flex align-items-center">
             <div>
                                        <h6 className="mb-1 font-weight-bold text-dark">
                                          {merchant.legalName || 'N/A'}
                                        </h6>
                                        <div className="d-flex align-items-center mb-1">
                                          <small className="text-muted">
                                            {merchant.industry || 'N/A'}
                                          </small>
                                        </div>
                                        <small className="text-muted">
                                          {businessAddress.city || 'N/A'}, {businessAddress.state || 'N/A'}
                                        </small>
                 </div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => handleViewApplication(application)}
                                        className="btn-icon mr-1"
                                        title="View Application Details"
                                        style={{ borderRadius: '50%', width: '28px', height: '28px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                      >
                                        <i className="ni ni-single-copy-04" style={{ fontSize: '0.8rem' }} />
                                      </Button>
                                      {status === 'INCOMPLETE' && (
                                        <Button
                                          color="primary"
                                          size="sm"
                                          onClick={() => navigate('/wizard/application/update', { state: { application } })}
                                          className="btn-icon mr-1"
                                          title="Edit Application"
                                          style={{ borderRadius: '50%', width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                          <i className="ni ni-settings-gear-65" style={{ fontSize: '0.8rem' }} />
                                        </Button>
                                      )}
                                      {(status === 'PENDING' || status === 'DUPLICATED') && (
                                        <Button
                                          color="primary"
                                          size="sm"
                                          onClick={() => openPromoteConfirm(application)}
                                          className="btn-icon mr-1"
                                          title="Promote to Deal"
                                          style={{ borderRadius: '50%', width: '36px', height: '36px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                          {promoteLoadingId === application.id ? (<Spinner size="sm" />) : (<i className="ni ni-send" />)}
                                        </Button>
                                      )}
                                      {status === 'DUPLICATED' && (
                                        <Button
                                          color="primary"
                                          size="sm"
                                          onClick={() => openDeleteConfirm(application)}
                                          className="btn-icon"
                                          title="Delete Application"
                                          style={{ borderRadius: '50%', width: '36px', height: '36px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                          <i className="ni ni-fat-remove" />
                                        </Button>
                                      )}
                                    </div>
               </div>

                                  <div className="d-flex align-items-center mb-3">
                                    <div>
                                      <div className="font-weight-bold text-dark mb-1">
                                        {firstOwner.firstName || 'N/A'} {firstOwner.lastName || 'N/A'}
                     </div>
                                      <div className="d-flex align-items-center">
                                        <small className="text-muted mr-3">
                                          FICO: {firstOwner.ficoScore || 'N/A'}
                                        </small>
                                        <small className="text-muted">
                                          {firstOwner.ownershipPercentage || 'N/A'}%
                                        </small>
                   </div>
                 </div>
               </div>

                                  <div className="row mb-3">
                                    <div className="col-6">
                                      <div className="d-flex align-items-center">
                                        <div>
                                          <small className="text-muted d-block">Amount</small>
                                          <span className="font-weight-bold text-dark">
                                            {formatCurrency(application.amount || 0)}
                                          </span>
                             </div>
                           </div>
                         </div>
                                    <div className="col-6">
                                      <div>
                                        <small className="text-muted d-block">Status</small>
                                        <Badge 
                                          color="light" 
                                          className="badge text-uppercase"
                                          style={{ fontSize: '0.6rem' }}
                                        >
                                          {String(application.status || 'N/A').toUpperCase()}
                                        </Badge>
                       </div>
                   </div>
                 </div>

                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                      <div>
                                        <small className="text-muted d-block">Created</small>
                                        <small className="font-weight-bold text-dark">
                                          {formatDate(application.createdDate || application.createdAt || application.created_at)}
                                       </small>
                                     </div>
                                 </div>
                                    <div className="text-right">
                                      <small className="text-muted d-block">Event</small>
                                      <small className="text-muted">
                                        {application.event || 'N/A'}
                                       </small>
                                     </div>
                                 </div>
                                </CardBody>
                              </Card>
                             </div>
                          );
                        })}
                           </div>
                         </div>
                  </>
                ))}
              
              {applications.length > 0 && (
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center px-3 py-2 border-top">
                  <div className="text-muted compact-text mb-2 mb-sm-0">
                    {totalElements > 0 
                      ? `Showing ${formatNumber(Math.min(currentPage * itemsPerPage + 1, totalElements))}-${formatNumber(Math.min((currentPage + 1) * itemsPerPage, totalElements))} of ${formatNumber(totalElements)}`
                      : `Showing ${formatNumber(applications.length)} applications`
                    }
                  </div>
                  <div className="d-flex align-items-center flex-wrap">
                    <Button
                      color="light"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={loading || currentPage === 0}
                      className="mr-1"
                      style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                    >
                      <i className="fas fa-chevron-left" />
                    </Button>
                    <div className="d-flex align-items-center">
                      {(() => {
                        const pages = [];
                        const maxVisiblePages = 5;
                        const total = Math.max(totalPages, 1);
                        let startPage = Math.max(1, (currentPage + 1) - Math.floor(maxVisiblePages / 2));
                        let endPage = Math.min(total, startPage + maxVisiblePages - 1);
                        if (endPage - startPage + 1 < maxVisiblePages) {
                          startPage = Math.max(1, endPage - maxVisiblePages + 1);
                        }
                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(i);
                        }
                        return pages.map(page => (
                          <Button
                            key={page}
                            color={page === (currentPage + 1) ? 'primary' : 'light'}
                            size="sm"
                            onClick={() => !loading && handlePageChange(page - 1)}
                            className="mx-1"
                            disabled={loading}
                            style={{ minWidth: '32px', padding: '4px 8px', fontSize: '0.75rem' }}
                          >
                            {loading && page === (currentPage + 1) ? (<Spinner size="sm" />) : page}
                          </Button>
                        ));
                      })()}
                    </div>
                    <Button
                      color="light"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={loading || (currentPage + 1 >= totalPages)}
                      className="ml-1"
                      style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                    >
                      <i className="fas fa-chevron-right" />
                    </Button>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
                 </Col>
       </Row>
       
      <ApplicationDetailsModal
        isOpen={modalOpen}
        toggle={toggleModal}
        applicationDetails={applicationDetails}
        detailsLoading={detailsLoading}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
      />

      <Modal isOpen={deleteConfirmOpen} toggle={closeDeleteConfirm} size="sm">
        <ModalHeader toggle={closeDeleteConfirm}>Confirm Deletion</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this application?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeDeleteConfirm} disabled={deleteLoading}>Cancel</Button>
          <Button color="danger" onClick={handleConfirmDelete} disabled={deleteLoading}>
            {deleteLoading ? (<Spinner size="sm" />) : 'Delete'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={promoteConfirmOpen} toggle={closePromoteConfirm} size="sm">
        <ModalHeader toggle={closePromoteConfirm}>Confirm Promotion</ModalHeader>
        <ModalBody>
          Are you sure you want to promote this application to a deal?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closePromoteConfirm} disabled={!!promoteLoadingId}>Cancel</Button>
          <Button color="success" onClick={() => promoteTargetApp && handlePromote(promoteTargetApp)} disabled={!!promoteLoadingId}>
            {promoteLoadingId ? (<Spinner size="sm" />) : 'Promote'}
          </Button>
        </ModalFooter>
      </Modal>

        <SuccessModal
          isOpen={successOpen}
          title={successTitle}
          message={successMessage}
          onClose={closeSuccess}
        />
     </Container>
     </>
   );
};

export default ApplicationsList;
